/** Startseite nach dem Login: Haus-Zustand auswählen. */
import { useEffect, useState } from "react";
import { HOUSES, type HouseKey, type HouseSummary } from "@hausplaner/model";
import { useStore } from "../state/store";
import { fetchHouses } from "../state/layouts";
import { VARIANTS } from "../engine/variants";
import type { WallDef } from "../engine/walls";
import { navigate, houseUrl } from "./router";
import { UserBox } from "./Menu";

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null);

export function StartPage() {
  const me = useStore((s) => s.me);
  const online = useStore((s) => s.online);
  const [houses, setHouses] = useState<HouseSummary[] | null>(null);
  useEffect(() => { void fetchHouses().then(setHouses); }, [me]);
  useEffect(() => { document.title = "Rosenstraße 30 · Hausplaner"; }, []);

  return (
    <div className="start">
      <header className="start-head">
        <div className="brand"><span className="k">Hausplaner</span></div>
        {online && me ? <UserBox compact /> : <span className="meta">Browser-Modus · ohne Server</span>}
      </header>
      <main className="start-main">
        <div className="start-title">
          <h1>Rosenstraße 30</h1>
          <p>Grüne Haushälfte · 7,75 × 11,00 m · vier Geschosse. Welchen Zustand willst du öffnen?</p>
        </div>
        <div className="house-grid">
          {HOUSES.map((h) => {
            const sum = houses?.find((x) => x.key === h.key);
            return (
              <a key={h.key} className="house-card" href={houseUrl(h.key)} id={"open-" + h.key}
                onClick={(e) => { e.preventDefault(); navigate(houseUrl(h.key)); }}>
                <PlanSketch house={h.key} />
                <div className="hc-body">
                  <div className="hc-kicker">{h.key === "bestand" ? "Ist-Zustand" : "Soll-Zustand"}</div>
                  <h2>{h.name}</h2>
                  <p>{h.description}</p>
                  <dl className="hc-meta">
                    {h.furniture && <><dt>Einrichtungen</dt><dd>{sum ? sum.layoutCount : "–"}</dd></>}
                    {!h.furniture && <><dt>Möbel</dt><dd>keine</dd></>}
                    <dt>Leitungspläne</dt><dd>{sum ? sum.planCount : "–"}</dd>
                    <dt>Leitungen</dt><dd>{sum ? sum.lineCount : "–"}</dd>
                    <dt>Anschlüsse</dt><dd>{sum ? sum.pointCount : "–"}</dd>
                    <dt>Geändert</dt><dd>{fmt(sum?.updatedAt ?? null) || "–"}</dd>
                  </dl>
                </div>
                <span className="hc-open">Öffnen →</span>
              </a>
            );
          })}
        </div>
      </main>
    </div>
  );
}

/** Grundriss-Skizze des Erdgeschosses aus den Wanddaten (Türen als Lücken, Fenster als Doppellinie) */
function PlanSketch({ house }: { house: HouseKey }) {
  const walls = VARIANTS[house].walls.filter((w) => w.f === 1 && w.x0 >= -0.01);
  const segs: { x: number; y: number; w: number; h: number; k: "wall" | "win" }[] = [];
  for (const w of walls as WallDef[]) {
    const alongX = w.x1 - w.x0 >= w.y1 - w.y0;
    const a0 = alongX ? w.x0 : w.y0, a1 = alongX ? w.x1 : w.y1;
    const cuts = (w.ops || []).map((o) => ({ a0: o.a0, a1: o.a1, door: o.b0 <= 0.001 })).sort((p, q) => p.a0 - q.a0);
    let cur = a0;
    const push = (u0: number, u1: number, k: "wall" | "win") => {
      if (u1 - u0 < 0.005) return;
      segs.push(alongX ? { x: u0, y: w.y0, w: u1 - u0, h: w.y1 - w.y0, k } : { x: w.x0, y: u0, w: w.x1 - w.x0, h: u1 - u0, k });
    };
    for (const c of cuts) { push(cur, c.a0, "wall"); if (!c.door) push(c.a0, c.a1, "win"); cur = Math.max(cur, c.a1); }
    push(cur, a1, "wall");
  }
  // y wächst nach hinten (Garten) → in der Skizze nach oben
  return (
    <svg className="sketch" viewBox="-0.6 -0.6 8.95 12.6" role="img" aria-label="Grundriss Erdgeschoss">
      <g transform="translate(0 11.4) scale(1 -1)">
        <rect x="0" y="0" width="7.75" height="11" className="sk-floor" />
        {segs.map((s, i) => <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h} className={s.k === "wall" ? "sk-wall" : "sk-win"} />)}
      </g>
      <text x="0" y="12.0" className="sk-label">EG · Straße ↓</text>
    </svg>
  );
}
